import background from "@assets/bg_player.jpg";
import icon from "@assets/hockeyplanner-icon.svg";
import {
	Button,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createFrontendTrpcClient, createFrontendAuthClient, validateEmail } from "@/frontend/utils";
import { FullPageLayout } from "@/frontend/components/FullPageLayout";
import { NotificationSnackbars } from "@/frontend/components/NotificationSnackbars";

const authClient = createFrontendAuthClient();

// A full-page login view that mirrors AuthDialog functionality, with a modern centered card
export default function LoginView({
	onAuthSuccess,
}: {
	onAuthSuccess?: () => void;
}) {
	const navigate = useNavigate();
	// Simplified: default to password login; secondary option triggers password reset
	const [authMode, setAuthMode] = useState<"password" | "no-password">(
		"password",
	);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [emailError, setEmailError] = useState<string | null>(null);
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);

	// Minimal tRPC client for session check
	const trpcClient = useMemo(() => createFrontendTrpcClient(), []);

	// If already logged in, jump directly to /events
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				await trpcClient.user.getOwn.query();
				if (!cancelled) navigate("/events", { replace: true });
			} catch {
				// not logged in — stay on login page
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [navigate, trpcClient]);


	const handleLogin = async () => {
		const eErr = validateEmail(email);
		setEmailError(eErr);
		if (eErr) return;
		try {
			await authClient.signIn.email({ email, password });
			// Verify session really exists before navigating
			try {
				await trpcClient.user.getOwn.query();
			} catch {
				setErrorMsg("Login fehlgeschlagen");
				setPassword("");
				return;
			}
			if (onAuthSuccess) onAuthSuccess();
		} catch (e: any) {
			setErrorMsg(e?.message ?? "Login fehlgeschlagen");
			setPassword("");
		}
	};

	const handlePasswordReset = async () => {
		const eErr = validateEmail(email) ?? null;
		if (eErr) {
			setEmailError(eErr);
			return;
		}
		try {
			// Ask backend to send reset email and redirect to our reset page when link clicked
			const redirectTo = `${window.location.origin}/reset-password`;
			// newer better-auth uses requestPasswordReset; fallback to forgetPassword if older
			const anyClient: any = authClient as any;
			if (anyClient.requestPasswordReset) {
				await anyClient.requestPasswordReset({ email, redirectTo });
			} else {
				await anyClient.forgetPassword({ email, redirectTo });
			}
			setSuccessMsg(
				"Falls ein Konto existiert, wurde eine E-Mail zum Setzen des Passworts versendet.",
			);
		} catch (e: any) {
			// The endpoint intentionally returns 200 to prevent email enumeration
			setSuccessMsg(
				"Falls ein Konto existiert, wurde eine E-Mail zum Setzen des Passworts versendet.",
			);
			setAuthMode("password");
		}
	};

	return (
		<FullPageLayout>
			<img
				src={icon}
				alt="HockeyPlanner"
				style={{ width: 72, height: 72 }}
			/>
			<Stack spacing={0.5} alignItems="center">
				<Typography variant="h5" sx={{ fontWeight: 700 }}>
					Anmelden
				</Typography>
				<Typography variant="body2" color="text.secondary" align="center">
					Wenn du dich schon mal eingetragen hast kannst du dich hier
					anmelden. <br />
					Ansonsten trage dich ein und registriere dich{" "}
					<a href="/events"> hier</a>.
				</Typography>
			</Stack>

			<ToggleButtonGroup
				exclusive
				value={authMode}
				onChange={(_, v) => v && setAuthMode(v)}
				size="small"
				data-testid="login-mode"
			>
				<ToggleButton value="password" data-testid="tab-password">
					Email & Passwort
				</ToggleButton>
				<ToggleButton value="no-password" data-testid="tab-no-password">
					Noch kein Passwort?
				</ToggleButton>
			</ToggleButtonGroup>

			<Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						authMode === "password"
							? handleLogin()
							: handlePasswordReset();
					}}
				>
					<TextField
						label="E-Mail"
						type="email"
						fullWidth
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							if (emailError) setEmailError(null);
						}}
						error={!!emailError}
						helperText={emailError || ""}
						inputProps={{ "data-testid": "login-email" }}
					/>
					{authMode === "password" ? (
						<>
							<TextField
								label="Passwort"
								type="password"
								fullWidth
								value={password}
								onChange={(e) => {
									setPassword(e.target.value);
									if (passwordError) setPasswordError(null);
								}}
								error={!!passwordError}
								helperText={passwordError || ""}
								sx={{ mt: 2 }}
								inputProps={{ "data-testid": "login-password" }}
							/>
							<Button
								variant="contained"
								size="large"
								type="submit"
								sx={{ mt: 2 }}
								data-testid="login-submit"
							>
								Login
							</Button>
						</>
					) : (
						<>
							<Typography
								variant="body2"
								color="text.secondary"
								align="left"
								sx={{ mt: 2 }}
							>
								Noch kein Passwort aber schon eingetragen? Gib deine
								E-Mail ein und wir schicken dir einen Link zum Setzen des
								Passworts.
							</Typography>
							<Button
								variant="contained"
								size="large"
								type="submit"
								sx={{ mt: 2 }}
								data-testid="login-send-reset-link"
							>
								Link zum Passwort setzen senden
							</Button>
						</>
					)}
				</form>
			</Stack>
			<NotificationSnackbars
				errorMsg={errorMsg}
				successMsg={successMsg}
				onCloseError={() => setErrorMsg(null)}
				onCloseSuccess={() => setSuccessMsg(null)}
			/>
		</FullPageLayout>
	);
}
